import { ParagraphNode } from 'lexical';
import { HeadingNode } from '@lexical/rich-text';

/**
 * Lexical 0.43 does not include ElementNode.__style (line-height, etc.) in
 * exportJSON / importJSON / exportDOM. We patch the prototypes of ParagraphNode
 * and HeadingNode directly so that __style round-trips through JSON AND is
 * applied to the exported HTML element (needed for PDF/print rendering).
 *
 * This module must be imported once before the editor is created.
 */

// ── Shared helper: apply __style rules with !important to a DOM element ─────
function applyStyleImportant(style, element) {
  for (const rule of style.split(';')) {
    const idx = rule.indexOf(':');
    if (idx === -1) continue;
    const prop = rule.slice(0, idx).trim();
    const val  = rule.slice(idx + 1).trim();
    if (prop && val) element.style.setProperty(prop, val, 'important');
  }
}

// ── ParagraphNode ────────────────────────────────────────────────────────────

// createDOM: apply __style on initial render (Lexical's ParagraphNode.createDOM
// never applies __style to the DOM, so line-height etc. wouldn't show in editor).
const _paragraphCreateDOM = ParagraphNode.prototype.createDOM;
ParagraphNode.prototype.createDOM = function (config, editor) {
  const dom = _paragraphCreateDOM.call(this, config, editor);
  if (this.__style) applyStyleImportant(this.__style, dom);
  return dom;
};

// updateDOM: keep DOM in sync when setStyle() is called after initial render.
// IMPORTANT: do NOT use dom.removeAttribute('style') here — Lexical stores
// text-align as a plain inline style (dom.style.textAlign) via FORMAT_ELEMENT_COMMAND,
// and wiping the entire attribute removes it. Instead, remove only the CSS properties
// that were in prevNode.__style, then apply the new ones on top.
const _paragraphUpdateDOM = ParagraphNode.prototype.updateDOM;
ParagraphNode.prototype.updateDOM = function (prevNode, dom, config) {
  const result = _paragraphUpdateDOM.call(this, prevNode, dom, config);
  if (prevNode.__style !== this.__style) {
    // Remove only the properties we previously owned.
    if (prevNode.__style) {
      for (const rule of prevNode.__style.split(';')) {
        const idx = rule.indexOf(':');
        if (idx === -1) continue;
        const prop = rule.slice(0, idx).trim();
        if (prop) dom.style.removeProperty(prop);
      }
    }
    if (this.__style) applyStyleImportant(this.__style, dom);
  }
  return result;
};

const _paragraphExportJSON = ParagraphNode.prototype.exportJSON;
ParagraphNode.prototype.exportJSON = function () {
  const json = _paragraphExportJSON.call(this);
  if (this.__style) json.style = this.__style;
  return json;
};

const _paragraphImportJSON = ParagraphNode.importJSON;
ParagraphNode.importJSON = function (serializedNode) {
  const node = _paragraphImportJSON.call(this, serializedNode);
  if (serializedNode.style) node.setStyle(serializedNode.style);
  return node;
};

// exportDOM: apply styles with !important for PDF/print HTML export.
const _paragraphExportDOM = ParagraphNode.prototype.exportDOM;
ParagraphNode.prototype.exportDOM = function (editor) {
  const result = _paragraphExportDOM.call(this, editor);
  if (this.__style && result.element) applyStyleImportant(this.__style, result.element);
  return result;
};

// ── HeadingNode ──────────────────────────────────────────────────────────────

// createDOM: apply __style on initial render (same issue as ParagraphNode).
const _headingCreateDOM = HeadingNode.prototype.createDOM;
HeadingNode.prototype.createDOM = function (config, editor) {
  const dom = _headingCreateDOM.call(this, config, editor);
  if (this.__style) applyStyleImportant(this.__style, dom);
  return dom;
};

// updateDOM: HeadingNode.updateDOM only handles tag changes; patch to also
// handle style changes when the tag stays the same.
// Same rule as ParagraphNode: remove only the properties we own, never the
// full attribute, so Lexical's inline text-align is not wiped.
const _headingUpdateDOM = HeadingNode.prototype.updateDOM;
HeadingNode.prototype.updateDOM = function (prevNode, dom, config) {
  const recreate = _headingUpdateDOM.call(this, prevNode, dom, config);
  // If recreate === true, createDOM will be called — style is handled there.
  if (!recreate && prevNode.__style !== this.__style) {
    if (prevNode.__style) {
      for (const rule of prevNode.__style.split(';')) {
        const idx = rule.indexOf(':');
        if (idx === -1) continue;
        const prop = rule.slice(0, idx).trim();
        if (prop) dom.style.removeProperty(prop);
      }
    }
    if (this.__style) applyStyleImportant(this.__style, dom);
  }
  return recreate;
};

const _headingExportJSON = HeadingNode.prototype.exportJSON;
HeadingNode.prototype.exportJSON = function () {
  const json = _headingExportJSON.call(this);
  if (this.__style) json.style = this.__style;
  return json;
};

const _headingImportJSON = HeadingNode.importJSON;
HeadingNode.importJSON = function (serializedNode) {
  const node = _headingImportJSON.call(this, serializedNode);
  if (serializedNode.style) node.setStyle(serializedNode.style);
  return node;
};

const _headingExportDOM = HeadingNode.prototype.exportDOM;
HeadingNode.prototype.exportDOM = function (editor) {
  const result = _headingExportDOM.call(this, editor);
  if (this.__style && result.element) applyStyleImportant(this.__style, result.element);
  return result;
};
